// Expected against actual, once a statement for the period is loaded: every Uber
// pay week from v_uber_payout_variance, every scheduled debit with a bank
// pattern, and the Sasol pattern beside the Tuesday rule. The findings, the
// running total and the tolerance test are Postgres's; this draws them.
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import {
  AMOUNT_UNKNOWN, formatDay, formatDayYear, formatZar,
  type DebitVarianceRow, type Petrol, type UberVarianceRow,
} from '../../utils/cashPlan'

function findingTone(r: UberVarianceRow): 'green' | 'amber' | 'red' | 'neutral' {
  if (r.within_tolerance) return 'green'
  if (/held over|catch-up/i.test(r.finding)) return 'amber'
  if (r.statement_covers) return 'red'
  return 'neutral'
}

export function UberVariance({ rows, tolerance }: { rows: UberVarianceRow[]; tolerance: number }) {
  return (
    <section className="rounded-lg border border-border bg-card">
      <h3 className="border-b border-border px-4 py-2 text-sm font-semibold">Uber payouts, expected against the bank</h3>
      <p className="px-4 pt-2 text-xs text-muted-foreground">
        Expected is net earnings less cash collected for the pay week. A week agrees within {tolerance}%. The running total nets out a
        second credit that was held back one week and paid in a later one.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[60rem] text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Pay week</th>
              <th className="px-3 py-2 text-right font-medium">Expected</th>
              <th className="px-3 py-2 text-right font-medium">Main credit</th>
              <th className="px-3 py-2 text-right font-medium">Second credit</th>
              <th className="px-3 py-2 text-right font-medium">Difference</th>
              <th className="px-3 py-2 text-right font-medium">Running</th>
              <th className="px-3 py-2 font-medium">Finding</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.week_start} className="border-t border-border align-top">
                <td className="px-3 py-2 whitespace-nowrap">{formatDay(r.week_start, false)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatZar(r.expected_total)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatZar(r.actual_main)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatZar(r.actual_secondary)}</td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                  {formatZar(r.variance_zar, 'Not loaded')}
                  {r.variance_pct !== null && <div className="text-xs text-muted-foreground">{r.variance_pct}%</div>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{formatZar(r.cumulative_variance_zar, '')}</td>
                <td className="px-3 py-2">
                  <Chip tone={findingTone(r)} className="whitespace-normal">{r.finding}</Chip>
                  {r.credits && <div className="mt-1 text-xs text-muted-foreground">{r.credits}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function DebitVariance({ rows }: { rows: DebitVarianceRow[] }) {
  return (
    <section className="rounded-lg border border-border bg-card">
      <h3 className="border-b border-border px-4 py-2 text-sm font-semibold">Scheduled debits, expected against the bank</h3>
      <p className="px-4 pt-2 text-xs text-muted-foreground">
        Each planned date in the three months to the last statement, against the matching debits from three days before to seven days
        after. Salaries, owners and EMP201 are paid as many separate payments and are not compared here.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[48rem] text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">Item</th>
              <th className="px-3 py-2 text-right font-medium">Expected</th>
              <th className="px-3 py-2 text-right font-medium">On the statement</th>
              <th className="px-3 py-2 text-right font-medium">Difference</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.schedule_id}-${r.due_date}-${i}`} className="border-t border-border align-top">
                <td className="px-3 py-2 whitespace-nowrap">{formatDay(r.due_date)}</td>
                <td className="px-3 py-2">{r.supplier}{r.actual_lines && <div className="text-xs text-muted-foreground">{r.actual_lines}</div>}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.expected_known ? formatZar(r.expected_zar) : AMOUNT_UNKNOWN}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatZar(r.actual_zar, 'Not found')}</td>
                <td className={cn('px-3 py-2 text-right tabular-nums', r.difference_zar !== null && Math.abs(r.difference_zar) > 1 && 'text-amber-700 dark:text-amber-300')}>
                  {r.difference_zar === null ? '' : formatZar(r.difference_zar)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function PetrolPattern({ petrol, warning }: { petrol: Petrol; warning: boolean }) {
  const s = petrol.sasol
  const b = petrol.band
  return (
    <section className="rounded-lg border border-border bg-card">
      <h3 className="border-b border-border px-4 py-2 text-sm font-semibold">Petrol: the Tuesday rule against what Sasol was actually paid</h3>
      <div className="grid gap-4 p-4 md:grid-cols-2">
        <div className="space-y-1 text-sm">
          <p className="font-medium">Actual Sasol spend {s.window_from ? `${formatDayYear(s.window_from)} to ${formatDayYear(s.window_to)}` : ''}</p>
          <p className="text-muted-foreground">
            {formatZar(s.spend_zar)} over {s.shifts ?? 0} shifts worked: {formatZar(s.per_shift_zar)} a shift.
          </p>
          <p className="font-medium pt-2">Band check</p>
          <p className="text-muted-foreground">
            {b.price_per_litre_zar !== null ? `Pump price R${Number(b.price_per_litre_zar).toFixed(2)} a litre from ${formatDayYear(b.price_from)}, ` : 'No pump price, '}
            budget share {b.budget_share !== null ? `${Math.round(Number(b.budget_share) * 1000) / 10}%` : 'not found'} of net earnings
            {b.net_per_shift_zar !== null ? ` of ${formatZar(b.net_per_shift_zar)} a shift` : ''}: {formatZar(b.per_shift_zar)} a shift.
          </p>
          <p className={cn('pt-2', warning ? 'font-medium text-rose-700 dark:text-rose-300' : 'text-muted-foreground')}>
            The plan uses the {petrol.basis === 'band' ? 'band check' : 'actual Sasol spend'}, {formatZar(petrol.chosen_per_shift_zar)} a shift.
            {petrol.disagreement_pct !== null ? ` The two disagree by ${Math.abs(petrol.disagreement_pct)}%.` : ''}
            {warning ? ' Change the basis in Settings if the Sasol payments no longer show all the petrol.' : ''}
          </p>
        </div>
        <div className="space-y-3 text-sm">
          <table className="w-full">
            <thead className="text-left text-xs text-muted-foreground">
              <tr><th className="py-1 font-medium">Week</th><th className="py-1 font-medium">Paid on</th><th className="py-1 text-right font-medium">Sasol</th><th className="py-1 text-right font-medium">Shifts</th></tr>
            </thead>
            <tbody>
              {s.by_week.map((w) => (
                <tr key={w.week_start} className="border-t border-border">
                  <td className="py-1">{formatDay(w.week_start, false)}</td>
                  <td className="py-1 text-muted-foreground">{w.days ?? 'No payment'}</td>
                  <td className="py-1 text-right tabular-nums">{formatZar(w.amount_zar)}</td>
                  <td className="py-1 text-right tabular-nums">{w.shifts}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground">
            By weekday: {s.by_weekday.length === 0 ? 'no Sasol payment in these weeks' : s.by_weekday.map((d) => `${d.weekday} ${d.payments}`).join(', ')}.
          </p>
        </div>
      </div>
    </section>
  )
}
